/* Market pulse — indices, movers and a sector heatmap.
 *
 * Three views of the same question: what is the market doing right now.
 *
 *   * Indices  — a stat row. Four numbers do not need a chart.
 *   * Movers   — ranked signed bars. Magnitude with direction, sorted.
 *   * Sectors  — a heatmap, area by constituent count, colour diverging around
 *                a neutral zero.
 *
 * The heatmap and the mover bars both carry direct labels with an explicit
 * sign, so direction never rests on colour alone.
 *
 * Instruments whose feed did not answer are EXCLUDED and counted, not shown as
 * unchanged. A stock that looks flat because its feed is dead would otherwise
 * sit quietly in the middle of a movers table looking like information.
 */
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { Panel } from '@/components/Panel'
import { useStore } from '@/store'
import { fmtPrice, isSimulatedSource } from '@/lib/format'
import { Empty, Heatmap, SignedBars, type BarDatum, type HeatCell } from '@/components/viz'
import type { IndexQuote, MoversResponse, SectorPerformance } from '@/types'

const REFRESH_MS = 60_000

type View = 'movers' | 'sectors'

export function MarketPulse() {
  const select = useStore((s) => s.selectInstrument)
  const [view, setView] = useState<View>('movers')
  const [indices, setIndices] = useState<IndexQuote[]>([])
  const [movers, setMovers] = useState<MoversResponse | null>(null)
  const [sectors, setSectors] = useState<SectorPerformance[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let timer: number | undefined

    const load = async () => {
      const results = await Promise.allSettled([
        api.indices(),
        api.movers('NSE', 8),
        api.sectors('NSE'),
      ])
      if (cancelled) return
      if (results[0].status === 'fulfilled') setIndices(results[0].value.items)
      if (results[1].status === 'fulfilled') setMovers(results[1].value)
      if (results[2].status === 'fulfilled') setSectors(results[2].value.sectors)
      setFailed(results.every((r) => r.status === 'rejected'))
      setLoading(false)
      timer = window.setTimeout(() => void load(), REFRESH_MS)
    }
    void load()
    return () => { cancelled = true; if (timer) window.clearTimeout(timer) }
  }, [])

  const moverBars: BarDatum[] = movers
    ? [...movers.gainers, ...movers.losers]
        .filter((m, i, arr) => arr.findIndex((x) => x.symbol === m.symbol) === i)
        .sort((a, b) => b.change_pct - a.change_pct)
        .map((m) => ({
          key: m.symbol, label: m.symbol, value: m.change_pct,
          sub: fmtPrice(m.last),
        }))
    : []

  const heatCells: HeatCell[] = sectors.map((s) => ({
    key: s.sector,
    label: s.sector,
    value: s.avg_change_pct,
    weight: s.constituents,
    // The constituent count travels with the average: a sector average from
    // two of eight members is a much weaker statement than one from all eight.
    sub: `${s.constituents} quoted · ${s.advancing}▲ ${s.declining}▼`,
  }))

  return (
    <Panel
      title="Market pulse"
      actions={
        <div style={{ display: 'flex', gap: 'var(--sp-1)' }}>
          {(['movers', 'sectors'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              aria-pressed={view === v}
              style={{
                padding: '1px var(--sp-2)',
                background: view === v ? 'var(--accent-bg)' : 'transparent',
                border: `1px solid ${view === v ? 'var(--accent)' : 'var(--border)'}`,
                color: view === v ? 'var(--accent)' : 'var(--text-muted)',
                borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-2xs)',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {v}
            </button>
          ))}
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>

        {/* Indices: four numbers, no chart. A chart here would be decoration. */}
        {indices.length > 0 && (
          <div style={{
            display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap',
            paddingBottom: 'var(--sp-2)', borderBottom: '1px solid var(--border)',
          }}>
            {indices.map((ix) => {
              // A value from the simulator feed is NOT an index level. Showing
              // a random-walk number in the same typeface as a real one is how
              // a trader ends up reading 49.63 as the Sensex.
              const simulated = ix.quoted && isSimulatedSource(ix.source)
              return (
                <div key={ix.instrument_id} style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{
                    fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>
                    {ix.symbol}
                  </span>
                  <span className="num" style={{
                    fontSize: 'var(--fs-md)', fontWeight: 600,
                    // A level that did not quote reads as a dash, never as zero
                    // or a stale number wearing a live typeface.
                    color: !ix.quoted ? 'var(--text-muted)'
                         : simulated ? 'var(--warn)' : 'var(--text-primary)',
                  }}>
                    {ix.quoted ? fmtPrice(ix.last) : '—'}
                  </span>
                  <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>
                    {!ix.quoted ? 'no live feed'
                      : simulated ? `SIMULATED (${ix.source})`
                      : ix.source}
                  </span>
                </div>
              )
            })}
          </div>
        )}

        {loading && <Empty>Loading market data…</Empty>}

        {!loading && failed && (
          <Empty>
            Market data is unavailable. The API could not be reached or no feed
            is configured for this venue.
          </Empty>
        )}

        {!loading && !failed && view === 'movers' && (
          movers && moverBars.length > 0 ? (
            <>
              <SignedBars
                data={moverBars}
                format={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`}
              />
              <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                {movers.quoted} of {movers.universe} instruments quoted.{' '}
                {movers.unquoted > 0 && (
                  <span style={{ color: 'var(--warn)' }}>
                    {movers.unquoted} excluded for want of a usable quote or
                    previous close — not shown as unchanged.
                  </span>
                )}
              </div>
              <details>
                <summary style={{
                  cursor: 'pointer', fontSize: 'var(--fs-2xs)',
                  color: 'var(--text-muted)',
                }}>
                  Table view
                </summary>
                <table className="qx" style={{ marginTop: 'var(--sp-2)' }}>
                  <thead>
                    <tr>
                      <th>Symbol</th><th>Sector</th>
                      <th className="r">Last</th><th className="r">Prev close</th>
                      <th className="r">Chg%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...movers.gainers, ...movers.losers]
                      .filter((m, i, a) => a.findIndex((x) => x.symbol === m.symbol) === i)
                      .sort((a, b) => b.change_pct - a.change_pct)
                      .map((m) => (
                        <tr key={m.symbol}
                            onClick={() => select(m.symbol)}
                            style={{ cursor: 'pointer' }}>
                          <td>{m.symbol}</td>
                          <td style={{ color: 'var(--text-muted)' }}>{m.sector ?? '—'}</td>
                          <td className="num r">{fmtPrice(m.last)}</td>
                          <td className="num r">{fmtPrice(m.prev_close)}</td>
                          <td className="num r" style={{
                            color: m.change_pct >= 0 ? 'var(--profit)' : 'var(--loss)',
                          }}>
                            {m.change_pct >= 0 ? '+' : ''}{m.change_pct.toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </details>
            </>
          ) : (
            <Empty>No instrument returned both a quote and a previous close.</Empty>
          )
        )}

        {!loading && !failed && view === 'sectors' && (
          heatCells.length > 0 ? (
            <>
              <Heatmap cells={heatCells} />
              <div style={{
                display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
                fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
              }}>
                <span>Loss</span>
                {['--div-neg-3', '--div-neg-2', '--div-neg-1', '--div-mid',
                  '--div-pos-1', '--div-pos-2', '--div-pos-3'].map((t) => (
                  <span key={t} style={{
                    width: 20, height: 9, background: `var(${t})`,
                    borderRadius: 2, display: 'inline-block',
                  }} />
                ))}
                <span>Gain</span>
                <span style={{ marginLeft: 'var(--sp-3)' }}>
                  Cell area is the number of constituents that quoted.
                </span>
              </div>
            </>
          ) : (
            <Empty>No sector could be computed from the instruments that quoted.</Empty>
          )
        )}
      </div>
    </Panel>
  )
}
